import cuttingmasterService from '../../../services/cuttingmasterService';
import filterService from '../../../services/filterService';

import {
    setCuttingMasterLoading,
    setCuttingMasterError,
    setCuttingMasterList,
    setCuttingMasterCurrent,
    setCuttingMasterPagination,
    setCuttingMasterSort,
    setCuttingMasterFilters,
    setCuttingMasterDefaultFilter,
    setCuttingMasterSettings,
    clearCuttingMasterCurrent,
    clearCuttingMasterError,
    setCuttingMasterFilterLoading,
    changeCuttingMasterFilterCondition,
    addCuttingMasterFilterCondition,
    updateCuttingMasterFilterConditionRule,
    updateCuttingMasterFilterConditionValue,
    removeCuttingMasterFilterCondition,
    setCuttingMasterSelected,
    resetCuttingMasterFiltersAndPagination
} from './cuttingmasterSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().cuttingmaster.isSettingsInitialized) {
            dispatch(setCuttingMasterLoading(true));
            const response = await cuttingmasterService.getSettings();

            // Set the settings
            dispatch(setCuttingMasterSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCuttingMasterDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCuttingMasterError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCuttingMasterLoading(false));
    }
};

export const fetchCuttingmasters = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().cuttingmaster;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setCuttingMasterLoading(true));
        const response = await cuttingmasterService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCuttingMasterList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCuttingmasters:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCuttingMasterError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCuttingMasterLoading(false));
    }
};

export const fetchCuttingMaster = (id) => async (dispatch) => {
    try {
        dispatch(setCuttingMasterLoading(true));
        const response = await cuttingmasterService.getOne(id);
        dispatch(setCuttingMasterCurrent(response.data));
    } catch (error) {
        dispatch(setCuttingMasterError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCuttingMasterLoading(false));
    }
};

export const createCuttingMaster = (data) => async (dispatch) => {
    try {
        const response = await cuttingmasterService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCuttingMaster = (id, data) => async (dispatch) => {
    try {
        const response = await cuttingmasterService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCuttingMaster = (id) => async (dispatch) => {
    try {
        dispatch(setCuttingMasterLoading(true));
        const response = await cuttingmasterService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCuttingMasterError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCuttingMasterLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCuttingMasterPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCuttingMasterSort(sort));
};

export const searchCuttingmasters = (query) => (dispatch, getState) => {
    const currentFilters = getState().cuttingmaster.filters;
    dispatch(setCuttingMasterFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCuttingMasterDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCuttingMasterFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCuttingMasterFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCuttingMasterFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCuttingMasterFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCuttingMasterFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCuttingMasterFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().cuttingmaster.settings;
            dispatch(setCuttingMasterSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCuttingMasterFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCuttingMasterFilterLoading(false));
        dispatch(setCuttingMasterError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCuttingMasterSelected(selectedRows));
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCuttingMasterFiltersAndPagination());
};